import { openModal, modalBody } from "./modal";
import type { StudentAssignment } from "../types";
import { esc } from "../utils/html";

/**
 * Detail-Modal fuer einen Einsatz im Schueler-Portal (Meine Planung + Dashboard).
 * Zeigt den von der Schule hinterlegten Schueler-Kommentar sowie Adresse/Kontakt
 * der Praktikumsstelle. Oeffnet sich, sobald es einen Kommentar ODER eine
 * Praktikumsstelle gibt (freie Eintraege mit Kommentar sind so erreichbar).
 */
export function showAssignmentDetail(a: StudentAssignment) {
  const hasComment = !!(a.studentNote && a.studentNote.trim());
  if (!a.practiceId && !hasComment) return;

  const sectionLabel = (text: string) =>
    `<div style="font-size:.7rem;text-transform:uppercase;letter-spacing:.08em;color:#9ca3af;font-weight:600;margin-bottom:.4rem">${text}</div>`;

  const commentSection = hasComment ? `
    <div style="margin-bottom:1.25rem">
      ${sectionLabel("Hinweis von der Schule")}
      <div style="font-size:.95rem;line-height:1.5;padding:.6rem .75rem;background:#eff6ff;border:1px solid #bfdbfe;border-radius:.375rem;color:#1e3a5f;white-space:pre-wrap;word-break:break-word">${esc(a.studentNote)}</div>
    </div>` : "";

  const hasPracticeInfo = !!(a.practiceStudentInfo && a.practiceStudentInfo.trim());
  const practiceInfoSection = hasPracticeInfo ? `
    <div style="margin-bottom:1.25rem">
      ${sectionLabel("Informationen zur Praktikumsstelle")}
      <div style="font-size:.95rem;line-height:1.5;padding:.6rem .75rem;background:#f8fafc;border:1px solid #e2e8f0;border-radius:.375rem;color:#334155;white-space:pre-wrap;word-break:break-word">${esc(a.practiceStudentInfo)}</div>
    </div>` : "";

  const addressSection = a.practiceAddress ? `
    <div style="margin-bottom:1.25rem">
      ${sectionLabel("Adresse")}
      <div id="detail-address" style="font-size:.95rem;line-height:1.5;cursor:pointer;padding:.5rem;background:#f9fafb;border-radius:.375rem;border:1px solid #e5e7eb" title="Adresse kopieren">
        ${esc(a.practiceAddress)}
      </div>
      <div id="detail-copy-hint" style="font-size:.75rem;color:#059669;margin-top:.3rem;min-height:1.1rem"></div>
    </div>` : "";

  const contactSection = (a.practiceContact || a.practicePhone || a.practiceContactEmail) ? `
    <div>
      ${sectionLabel("Ansprechpartner")}
      ${a.practiceContact ? `<div style="font-size:.95rem;font-weight:500;margin-bottom:.5rem">${esc(a.practiceContact)}</div>` : ""}
      ${a.practicePhone ? `<a href="tel:${esc(a.practicePhone)}" style="display:flex;align-items:center;gap:.6rem;padding:.75rem 0;border-top:1px solid #f3f4f6;color:#374151;text-decoration:none;font-size:.9rem">
        <span style="font-size:1rem">📞</span><span>${esc(a.practicePhone)}</span>
      </a>` : ""}
      ${a.practiceContactEmail ? `<a href="mailto:${esc(a.practiceContactEmail)}" style="display:flex;align-items:center;gap:.6rem;padding:.75rem 0;border-top:1px solid #f3f4f6;color:#374151;text-decoration:none;font-size:.9rem">
        <span style="font-size:1rem">✉️</span><span>${esc(a.practiceContactEmail)}</span>
      </a>` : ""}
    </div>` : "";

  const title = a.practiceName || a.departmentName || "Einsatz";

  openModal(title, `
    <div style="padding:.25rem 0">
      ${commentSection}
      ${practiceInfoSection}
      ${addressSection}
      ${contactSection}
      ${!commentSection && !practiceInfoSection && !addressSection && !contactSection ? `<p style="color:#9ca3af;font-size:.9rem">Keine Details verfügbar.</p>` : ""}
    </div>`);

  if (a.practiceAddress) {
    const addrEl = modalBody().querySelector("#detail-address");
    const hint = modalBody().querySelector("#detail-copy-hint") as HTMLElement | null;
    addrEl?.addEventListener("click", () => {
      navigator.clipboard.writeText(a.practiceAddress!).then(() => {
        if (hint) { hint.textContent = "Kopiert!"; setTimeout(() => { hint.textContent = ""; }, 1500); }
      }).catch(() => {});
    });
  }
}
